import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Document, Page } from 'react-pdf';
import { pdfjs } from 'react-pdf';
import { DndContext, useDraggable } from '@dnd-kit/core';
import { CSS } from '@dnd-kit/utilities';
import { Mail, Move, Send, Plus, X, Loader2, CheckCircle2, Link2, Copy, Users, RefreshCw, AlertTriangle, QrCode } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Switch } from '../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';

import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';

pdfjs.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.js';

function DraggableField({ field, scale, onRemove }) {
  const { attributes, listeners, setNodeRef, transform } = useDraggable({
    id: field.id,
  });

  const style = {
    transform: CSS.Translate.toString(transform),
    left: `${field.x * scale}px`,
    top: `${field.y * scale}px`,
    width: `${field.width * scale}px`,
    height: `${field.height * scale}px`,
    position: 'absolute'
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      data-testid={`draggable-field-${field.id}`}
      className="signature-field"
    >
      <div {...listeners} {...attributes} style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <span className="text-xs">{field.text}</span>
      </div>
      <button
        data-testid={`remove-field-${field.id}`}
        onClick={(e) => {
          e.stopPropagation();
          onRemove(field.id);
        }}
        className="absolute -top-2 -right-2 h-6 w-6 bg-red-600 text-white rounded-full flex items-center justify-center hover:bg-red-700 transition-colors z-10"
        style={{ pointerEvents: 'auto' }}
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  );
}

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function ConfigureDocument({ documentData }) {
  const navigate = useNavigate();
  const [numPages, setNumPages] = useState(null);
  const [signatureFields, setSignatureFields] = useState(documentData.signature_fields || []);
  const [senderEmail, setSenderEmail] = useState('');
  const [recipients, setRecipients] = useState([{ email: '', name: '', role: '' }]);
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [pageWidth, setPageWidth] = useState(800);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [sentEmails, setSentEmails] = useState([]);
  const [showLinkModal, setShowLinkModal] = useState(false);
  const [documentLink, setDocumentLink] = useState('');
  const [isBeschlussprotokoll, setIsBeschlussprotokoll] = useState(false);
  const [enableVerificationQR, setEnableVerificationQR] = useState(false);
  const [pdfError, setPdfError] = useState(false);
  const [pdfRetryCount, setPdfRetryCount] = useState(0);
  const [pdfUrl, setPdfUrl] = useState(null);

  const MAX_RETRIES = 3;

  // Generate PDF URL with cache-busting
  useEffect(() => {
    const timestamp = Date.now();
    setPdfUrl(`${API}/document/${documentData.document_id}/pdf?t=${timestamp}&retry=${pdfRetryCount}`);
    setPdfError(false);
  }, [documentData.document_id, pdfRetryCount]);

  // Auto-retry on error
  useEffect(() => {
    if (pdfError && pdfRetryCount < MAX_RETRIES) {
      const timer = setTimeout(() => {
        setPdfRetryCount(prev => prev + 1);
      }, 1500);
      return () => clearTimeout(timer);
    }
  }, [pdfError, pdfRetryCount]);

  const handlePdfError = (error) => {
    console.error('PDF load error:', error);
    setPdfError(true);
  };

  const handleRetryPdf = () => {
    setPdfRetryCount(prev => prev + 1);
    setPdfError(false);
  };

  useEffect(() => {
    updatePageWidth();
    window.addEventListener('resize', updatePageWidth);
    return () => window.removeEventListener('resize', updatePageWidth);
  }, []);

  const updatePageWidth = () => {
    // For mobile: use almost full width, for desktop: limit to 800px
    const isMobile = window.innerWidth < 768;
    const maxWidth = isMobile 
      ? window.innerWidth - 32  // 16px padding on each side
      : Math.min(window.innerWidth - 400, 800);
    setPageWidth(maxWidth);
  };

  const handleDragEnd = (event) => {
    const { active, delta } = event;
    const fieldId = active.id;
    const scale = pageWidth / 595;
    
    const updatedFields = signatureFields.map(field => {
      if (field.id === fieldId) {
        return { 
          ...field, 
          x: field.x + (delta.x / scale), 
          y: field.y + (delta.y / scale) 
        };
      }
      return field;
    });
    setSignatureFields(updatedFields);
  };

  const addSignatureField = () => {
    const newField = {
      id: `field_${Date.now()}`,
      page: 0,
      x: 100,
      y: 100,
      width: 200,
      height: 60,
      text: 'Neue Unterschrift',
      signed: false
    };
    setSignatureFields([...signatureFields, newField]);
  };

  const removeField = (fieldId) => {
    setSignatureFields(signatureFields.filter(f => f.id !== fieldId));
  };

  const addRecipientField = () => {
    setRecipients([...recipients, { email: '', name: '', role: '' }]);
  };

  const updateRecipient = (index, field, value) => {
    const updated = [...recipients];
    updated[index] = { ...updated[index], [field]: value };
    setRecipients(updated);
  };

  const removeRecipient = (index) => {
    setRecipients(recipients.filter((_, i) => i !== index));
  };

  const handleSaveAndGetLink = async () => {
    if (!senderEmail) {
      toast.error('Bitte geben Sie Ihre E-Mail-Adresse ein');
      return;
    }

    setSending(true);

    try {
      await axios.put(`${API}/document/${documentData.document_id}/fields`, signatureFields);

      await axios.put(`${API}/document/${documentData.document_id}/save`, {
        sender_email: senderEmail,
        is_beschlussprotokoll: isBeschlussprotokoll,
        enable_verification_qr: enableVerificationQR,
        recipients: recipients.filter(r => r.email).map(r => ({
          email: r.email,
          name: r.name || '',
          role: r.role || ''
        }))
      });

      const link = `${window.location.origin}/sign/${documentData.document_id}`;
      setDocumentLink(link);
      setShowLinkModal(true);
      toast.success('Dokument gespeichert!');
    } catch (error) {
      toast.error('Fehler beim Speichern des Dokuments');
      console.error(error);
    } finally {
      setSending(false);
    }
  };

  const copyLinkToClipboard = () => {
    navigator.clipboard.writeText(documentLink);
    toast.success('Link in Zwischenablage kopiert!');
  };

  const handleSend = async () => {
    if (!senderEmail) {
      toast.error('Bitte geben Sie Ihre E-Mail-Adresse ein');
      return;
    }

    const validRecipients = recipients.filter(r => r.email.trim() !== '');
    if (validRecipients.length === 0) {
      toast.error('Bitte fügen Sie mindestens einen Empfänger hinzu');
      return;
    }

    setSending(true);

    try {
      await axios.put(`${API}/document/${documentData.document_id}/fields`, signatureFields);

      // First update document with beschlussprotokoll setting and recipient info
      await axios.put(`${API}/document/${documentData.document_id}/save`, {
        sender_email: senderEmail,
        is_beschlussprotokoll: isBeschlussprotokoll,
        recipients: validRecipients.map(r => ({
          email: r.email,
          name: r.name || '',
          role: r.role || ''
        }))
      });

      await axios.post(`${API}/document/${documentData.document_id}/send`, {
        recipient_emails: validRecipients.map(r => r.email),
        sender_email: senderEmail,
        message: message || null,
        enable_verification_qr: enableVerificationQR
      });

      setSentEmails(validRecipients.map(r => r.email));
      setShowSuccessModal(true);
    } catch (error) {
      toast.error('Fehler beim Versenden des Dokuments');
      console.error(error);
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="min-h-screen bg-zinc-50">
      <header className="border-b border-zinc-200 bg-white/80 backdrop-blur-xl">
        <div className="container mx-auto px-4 sm:px-6 py-4">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 sm:gap-0">
            <div>
              <h1 className="text-xl sm:text-2xl font-bold tracking-tight" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
                Dokument konfigurieren
              </h1>
              <p className="text-zinc-600 text-sm mt-1">{documentData.filename}</p>
            </div>
            <div className="flex items-center gap-3 sm:gap-4 w-full sm:w-auto">
              <div className="text-right flex-shrink-0">
                <div className="text-xs text-zinc-500 uppercase tracking-wide">Dokument-ID</div>
                <div className="text-sm font-mono font-medium text-zinc-700" data-testid="document-id">
                  {documentData.document_id.slice(0, 8)}
                </div>
              </div>
              <div className="h-8 w-px bg-zinc-200 hidden sm:block"></div>
              <Button
                data-testid="back-to-hvkurz-button"
                variant="outline"
                size="sm"
                onClick={() => window.open('https://www.hvkurz.de', '_blank')}
                className="text-xs sm:text-sm"
              >
                <span className="hidden sm:inline">zurück zur Verwaltung</span>
                <span className="sm:hidden">Verwaltung</span>
              </Button>
            </div>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-4 sm:px-6 py-6 sm:py-12">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 sm:gap-8">
          {/* PDF Preview with draggable fields */}
          <div className="lg:col-span-2 order-2 lg:order-1">
            <Card className="p-4 sm:p-6">
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 sm:gap-0 mb-4">
                <h3 className="text-lg font-semibold">PDF-Vorschau</h3>
                <Button
                  data-testid="add-signature-field-button"
                  onClick={addSignatureField}
                  variant="outline"
                  size="sm"
                  className="gap-2 w-full sm:w-auto"
                >
                  <Plus className="h-4 w-4" />
                  Feld hinzufügen
                </Button>
              </div>
              <p className="text-sm text-zinc-600 mb-4 sm:mb-6 flex items-center gap-2">
                <Move className="h-4 w-4" />
                Felder können verschoben werden
              </p>
              <div className="flex justify-center overflow-x-auto">
                <DndContext onDragEnd={handleDragEnd}>
                  <div className="pdf-container bg-white shadow-lg rounded-sm relative" data-testid="pdf-preview">
                    {pdfError && pdfRetryCount >= MAX_RETRIES ? (
                      <div className="flex flex-col items-center justify-center p-12 min-h-[400px]">
                        <AlertTriangle className="h-12 w-12 text-amber-500 mb-4" />
                        <p className="text-zinc-600 mb-4 text-center">PDF konnte nicht geladen werden</p>
                        <Button
                          onClick={handleRetryPdf}
                          variant="outline"
                          className="gap-2"
                        >
                          <RefreshCw className="h-4 w-4" />
                          Neu laden
                        </Button>
                      </div>
                    ) : (
                      <Document
                        file={pdfUrl}
                        onLoadSuccess={({ numPages }) => {
                          setNumPages(numPages);
                          setPdfError(false);
                        }}
                        onLoadError={handlePdfError}
                        loading={
                          <div className="flex flex-col items-center justify-center p-12 min-h-[400px]">
                            <Loader2 className="h-8 w-8 animate-spin text-zinc-600" />
                            {pdfRetryCount > 0 && (
                              <p className="text-sm text-zinc-500 mt-2">Lade PDF... (Versuch {pdfRetryCount + 1})</p>
                            )}
                          </div>
                        }
                      >
                        {Array.from(new Array(numPages), (el, index) => (
                          <div key={`page_${index + 1}`} className="relative mb-4" data-testid={`config-page-${index}`}>
                            <Page
                              pageNumber={index + 1}
                              width={pageWidth}
                              renderTextLayer={false}
                              renderAnnotationLayer={false}
                            />
                            {signatureFields
                              .filter(field => field.page === index)
                              .map(field => {
                                const scale = pageWidth / 595;
                                return (
                                  <DraggableField
                                    key={field.id}
                                    field={field}
                                    scale={scale}
                                    onRemove={removeField}
                                  />
                                );
                              })}
                          </div>
                        ))}
                      </Document>
                    )}
                  </div>
                </DndContext>
              </div>
            </Card>
          </div>

          {/* Email Configuration */}
          <div className="space-y-4 sm:space-y-6 order-1 lg:order-2">
            {/* Beschlussprotokoll Toggle */}
            <Card className="p-4 bg-orange-50 border-orange-200">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Users className="h-5 w-5 text-orange-600" />
                  <div>
                    <Label htmlFor="beschlussprotokoll-toggle" className="font-semibold text-zinc-800 cursor-pointer">
                      Beschlussprotokoll
                    </Label>
                    <p className="text-xs text-zinc-600 mt-0.5">
                      Aktiviert Rollenauswahl für Unterzeichner
                    </p>
                  </div>
                </div>
                <Switch
                  id="beschlussprotokoll-toggle"
                  data-testid="beschlussprotokoll-toggle"
                  checked={isBeschlussprotokoll}
                  onCheckedChange={setIsBeschlussprotokoll}
                />
              </div>
            </Card>

            {/* Verification QR Toggle */}
            <Card className="p-4 bg-blue-50 border-blue-200">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <QrCode className="h-5 w-5 text-blue-600" />
                  <div>
                    <Label htmlFor="verification-qr-toggle" className="font-semibold text-zinc-800 cursor-pointer">
                      Signatur-Verifikation
                    </Label>
                    <p className="text-xs text-zinc-600 mt-0.5">
                      QR-Code zur Online-Verifikation im PDF
                    </p>
                  </div>
                </div>
                <Switch
                  id="verification-qr-toggle"
                  data-testid="verification-qr-toggle"
                  checked={enableVerificationQR}
                  onCheckedChange={setEnableVerificationQR}
                />
              </div>
            </Card>

            <Card className="p-6">
              <div className="space-y-4">
                <div>
                  <Label htmlFor="sender-email">Ihre E-Mail-Adresse *</Label>
                  <Input
                    id="sender-email"
                    data-testid="sender-email-input"
                    type="email"
                    placeholder="ihre.email@example.com"
                    value={senderEmail}
                    onChange={(e) => setSenderEmail(e.target.value)}
                    className="mt-2"
                  />
                  <p className="text-xs text-zinc-500 mt-1">
                    Das unterschriebene Dokument wird an diese Adresse gesendet
                  </p>
                </div>

                <div>
                  <div className="flex items-center justify-between mb-2">
                    <Label>Empfänger *</Label>
                    <Button
                      data-testid="add-recipient-button"
                      onClick={addRecipientField}
                      variant="ghost"
                      size="sm"
                      className="h-8 gap-1"
                    >
                      <Plus className="h-4 w-4" />
                      Hinzufügen
                    </Button>
                  </div>
                  <div className="space-y-3">
                    {recipients.map((recipient, index) => (
                      <div key={index} className="p-3 bg-zinc-50 rounded-lg border border-zinc-200 space-y-2">
                        <div className="flex gap-2">
                          <Input
                            data-testid={`recipient-email-${index}`}
                            type="email"
                            placeholder="E-Mail-Adresse *"
                            value={recipient.email}
                            onChange={(e) => updateRecipient(index, 'email', e.target.value)}
                            className="flex-1"
                          />
                          {recipients.length > 1 && (
                            <Button
                              data-testid={`remove-recipient-${index}`}
                              onClick={() => removeRecipient(index)}
                              variant="outline"
                              size="icon"
                              className="flex-shrink-0"
                            >
                              <X className="h-4 w-4" />
                            </Button>
                          )}
                        </div>
                        <div className="flex gap-2">
                          <Input
                            data-testid={`recipient-name-${index}`}
                            type="text"
                            placeholder="Name (optional)"
                            value={recipient.name}
                            onChange={(e) => updateRecipient(index, 'name', e.target.value)}
                            className="flex-1"
                          />
                          {isBeschlussprotokoll && (
                            <Select 
                              value={recipient.role} 
                              onValueChange={(value) => updateRecipient(index, 'role', value)}
                            >
                              <SelectTrigger 
                                className="flex-1" 
                                data-testid={`recipient-role-${index}`}
                              >
                                <SelectValue placeholder="Rolle wählen..." />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="Mitglied der Gemeinschaft">Mitglied der Gemeinschaft</SelectItem>
                                <SelectItem value="für den Verwaltungsbeirat">für den Verwaltungsbeirat</SelectItem>
                              </SelectContent>
                            </Select>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                  <p className="text-xs text-zinc-500 mt-2">
                    {isBeschlussprotokoll 
                      ? 'Name und Rolle werden beim Unterzeichner vorausgefüllt'
                      : 'Der Name wird beim Unterzeichner vorausgefüllt'}
                  </p>
                </div>

                <div>
                  <Label htmlFor="message">Nachricht (optional)</Label>
                  <Textarea
                    id="message"
                    data-testid="message-textarea"
                    placeholder="Fügen Sie eine persönliche Nachricht hinzu..."
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    rows={4}
                    className="mt-2"
                  />
                </div>
              </div>
            </Card>

            <Card className="p-6 bg-zinc-50 border-zinc-300">
              <h4 className="font-semibold mb-2 flex items-center gap-2">
                <Mail className="h-5 w-5 text-orange-600" />
                Informationen
              </h4>
              <ul className="text-sm text-zinc-600 space-y-2">
                <li>• Empfänger erhalten einen Link zum Unterschreiben</li>
                <li>• Der Link ist 30 Tage gültig</li>
                <li>• Nach allen Unterschriften erhalten Sie das PDF per E-Mail</li>
                <li>• Es wurden {signatureFields.length} Unterschriftsfeld(er) erkannt</li>
              </ul>
            </Card>

            <div className="space-y-3">
              <Button
                data-testid="send-document-button"
                onClick={handleSend}
                disabled={sending}
                className="w-full bg-zinc-950 text-white hover:bg-zinc-800 h-12 gap-2"
              >
                {sending ? (
                  <>
                    <Loader2 className="h-5 w-5 animate-spin" />
                    Wird gesendet...
                  </>
                ) : (
                  <>
                    <Send className="h-5 w-5" />
                    Dokument versenden
                  </>
                )}
              </Button>

              <Button
                data-testid="save-and-get-link-button"
                onClick={handleSaveAndGetLink}
                disabled={sending}
                variant="outline"
                className="w-full h-12 gap-2 border-2"
              >
                {sending ? (
                  <>
                    <Loader2 className="h-5 w-5 animate-spin" />
                    Wird gespeichert...
                  </>
                ) : (
                  <>
                    <Link2 className="h-5 w-5" />
                    Speichern & Link abrufen
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Success Modal */}
      {showSuccessModal && (
        <Dialog open={showSuccessModal} onOpenChange={() => {}}>
          <DialogContent className="max-w-md" data-testid="success-modal">
            <DialogHeader>
              <DialogTitle className="text-center text-2xl" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
                <div className="flex flex-col items-center gap-4 mb-4">
                  <div className="h-20 w-20 bg-green-100 rounded-full flex items-center justify-center animate-bounce">
                    <CheckCircle2 className="h-12 w-12 text-green-600" />
                  </div>
                  Erfolgreich versendet!
                </div>
              </DialogTitle>
            </DialogHeader>

            <div className="space-y-4">
              <p className="text-center text-zinc-600">
                Das Dokument wurde erfolgreich an folgende E-Mail-Adressen versendet:
              </p>

              <div className="space-y-2">
                {sentEmails.map((email, index) => (
                  <div 
                    key={index}
                    className="flex items-center gap-3 p-3 bg-green-50 border border-green-200 rounded-lg animate-fade-in"
                    style={{ animationDelay: `${index * 150}ms` }}
                  >
                    <div className="h-6 w-6 bg-green-500 rounded-full flex items-center justify-center flex-shrink-0">
                      <CheckCircle2 className="h-4 w-4 text-white" />
                    </div>
                    <span className="text-sm font-medium text-green-800">{email}</span>
                  </div>
                ))}
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-sm text-blue-800">
                <strong>Was passiert jetzt?</strong>
                <ul className="mt-2 space-y-1 ml-4 list-disc">
                  <li>Die Empfänger erhalten einen Link zum Unterschreiben</li>
                  <li>Nach allen Unterschriften erhalten Sie das fertige PDF per E-Mail</li>
                  <li>Der Link ist 30 Tage gültig</li>
                </ul>
              </div>

              <Button
                data-testid="back-home-button"
                onClick={() => {
                  setShowSuccessModal(false);
                  navigate('/');
                }}
                className="w-full bg-zinc-950 text-white hover:bg-zinc-800 h-12"
              >
                Zur Startseite
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Link Modal */}
      {showLinkModal && (
        <Dialog open={showLinkModal} onOpenChange={setShowLinkModal}>
          <DialogContent className="max-w-2xl" data-testid="link-modal">
            <DialogHeader>
              <DialogTitle className="text-center text-2xl" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
                <div className="flex flex-col items-center gap-4 mb-4">
                  <div className="h-20 w-20 bg-blue-100 rounded-full flex items-center justify-center">
                    <Link2 className="h-12 w-12 text-blue-600" />
                  </div>
                  Dokument gespeichert!
                </div>
              </DialogTitle>
            </DialogHeader>

            <div className="space-y-4">
              <p className="text-center text-zinc-600">
                Ihr Dokument wurde gespeichert. Teilen Sie den folgenden Link mit den Personen, die unterschreiben sollen:
              </p>

              <div className="bg-zinc-50 border-2 border-zinc-300 rounded-lg p-4">
                <div className="flex items-center gap-3">
                  <input
                    type="text"
                    value={documentLink}
                    readOnly
                    className="flex-1 bg-white border border-zinc-300 rounded px-3 py-2 text-sm font-mono"
                    onClick={(e) => e.target.select()}
                  />
                  <Button
                    onClick={copyLinkToClipboard}
                    variant="outline"
                    className="gap-2 flex-shrink-0"
                  >
                    <Copy className="h-4 w-4" />
                    Kopieren
                  </Button>
                </div>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-sm text-blue-800">
                <strong>Wichtig:</strong>
                <ul className="mt-2 space-y-1 ml-4 list-disc">
                  <li>Teilen Sie diesen Link mit allen Personen, die unterschreiben sollen</li>
                  <li>Der Link ist 30 Tage gültig</li>
                  <li>Nach allen Unterschriften erhalten Sie das fertige PDF per E-Mail an: <strong>{senderEmail}</strong></li>
                  <li>Sie können den Link mehrfach teilen</li>
                </ul>
              </div>

              <div className="flex gap-3">
                <Button
                  onClick={() => setShowLinkModal(false)}
                  variant="outline"
                  className="flex-1 h-12"
                >
                  Schließen
                </Button>
                <Button
                  onClick={() => navigate('/')}
                  className="flex-1 bg-zinc-950 text-white hover:bg-zinc-800 h-12"
                >
                  Zur Startseite
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}